<template lang="html">
  <div class="product-detail">
    <FullScreenSpinner v-if="fullScreenLoading" />
    <Header />
    <ListingPreview
      v-if="preview"
      :previewData="$data"
      @exit-preview="preview = false"
      @open-contact-modal="openContactModal"
      @open-report-modal="openReportModal"
    />

    <div v-show="!preview" class="dashboard-content-container margin-auto">
      <Navigation
        @save-draft="saveDraft"
        @open-preview="preview = true"
        @publish-draft="openPublishDraftModal"
        @become-fixed="addPaddingTop"
        @become-unfixed="removePaddingTop"
        @back-to-listing="backToListing"
        :preupload="preupload"
        :needReplace="needReplace"
        :isSuspendedVendor="isSuspendedVendor"
      />
      <div class="dashboard-content-middle single-section page-container" ref="productDetailFormContainer">
        <TimeStamp
          :updatedAt="updated_at"
          :publishedAt="published_at"
          :status="status"
          :loading="loading"
          @destroy="openDestroyModal"
          @unpublish="openUnpublishModal"
          :isSuspendedVendor="isSuspendedVendor"
          v-if="!preupload"
        />

        <div class="section">
          <h2 class="black section-field no-border-top">Detailed Product Listing</h2>
          <div class="line-break-gray" />
          <div class="gap-3x" />
          <div v-if="loading">
          <div class="gap-3x" />
            <Spinner />
            <div class="gap-3x" />
          </div>
          <div class="black" v-if="!loading">
            <div class="section-field product-form gray-border">
              <ProfileImageContainer
                :imageUrl="imageURLs[0]"
                @manage-image="openImageUploadModal"
              />
              <div class="error-message" v-if="errors.images">{{errors.images}}</div>
              <div class="gap-3x" />
              <InputWithErrorV2
                @input:value="(value) => { this.name = value }"
                :value="name"
                :placeholder="'Product Name'"
                :error="errors.name"
                klass="big-body"
              />
              <div class="gap-1x" />
              <div :class="['small-text', errors.name ? 'error-message' : '']">
                Text Count: {{nameTextCount}}
              </div>
              <div class="gap-1x" />
              <div class="big-body">By {{organizationName}}</div>
              <div class="gap-3x" />
              <InputWithErrorV2
                @input:value="(value) => { this.sellingpoint = value.split(' · ') }"
                :value="formattedSellingpoint"
                :placeholder="'Selling points - 50 characters maximum'"
                :error="errors.sellingpoint"
              />
              <div class="gap-1x" v-if="!errors.sellingpoint" />
              <div :class="['small-text', errors.sellingpoint ? 'error-message' : '']">
                Text Count: {{this.formattedSellingpoint.length}}
              </div>
              <div class="gap-3x" />
              <div class="line-break-gray" />
              <div class="gap-3x" />
              <div class="flex-box">
                <SymbiumButton
                  :text="contactButtonText"
                  :onClick="openContactModal"
                  kclass="flex-grow-1 contact-button margin-right-2x"
                />
                <SymbiumButton
                  text="ADU Report"
                  :onClick="openReportModal"
                  kclass="flex-grow-1 contact-button"
                />
              </div>

              <PreviewSection title="Overview">
                <AutoResizeInput
                  :placeholder="'Product description — 1500 characters maximum'"
                  v-bind:value.sync="description"
                  :error="errors.description"
                />
              </PreviewSection>

              <PreviewSection title="Cost">
                <QuestionGroup klass="product-detail-input" question="Estimated Cost" :noBorder="true" :error="errors.cost">
                  <money v-model="materialfinish[0].cost" v-bind="moneyOption" />
                </QuestionGroup>
                <QuestionGroup :error="errors.producttype" klass="product-detail-input" question="Product Type" :noBorder="true">
                  <div id="product-type-dropdown"></div>
                </QuestionGroup>
                <QuestionGroup :error="errors.includes" klass="product-detail-input" question="Includes" :noBorder="true">
                  <div @click="() => openSelectModal(includesMaterialData)">
                    <div>{{formattedDetails(includesMaterialData)}}</div>
                    <div class="gap-1x" />
                    <div class="link active medium-small-text bold">Edit</div>
                  </div>
                </QuestionGroup>
              </PreviewSection>

              <PreviewSection title="Specifications">
                <QuestionGroup
                  klass="product-detail-input"
                  question="Building Area"
                  :noBorder="true"
                  :error="errors.area" 
                >
                  <div class="flex-box vertically-centered">
                    <div id="building-area-field"></div>
                    {{area ? '' : ''}}
                  </div>
                </QuestionGroup>
                <QuestionGroup
                  klass="product-detail-input"
                  question="Building Height"
                  :noBorder="true"
                  :error="errors.maxHeight" 
                >
                  <div class="flex-box vertically-centered">
                    <div id="building-height-field"></div>
                    {{maxHeight ? '' : ''}}
                  </div>
                </QuestionGroup>
                <QuestionGroup
                  klass="product-detail-input"
                  question="Rooms"
                  :noBorder="true"
                  :error="errors.rooms"
                >
                  <div @click="openRoomSelectModal">
                    <div v-if="formattedRooms">
                      {{formattedRooms}}
                    </div>
                    <div class="gap-1x" />
                    <div class="link active medium-small-text bold">Edit</div>
                  </div>
                </QuestionGroup>

                <div class="gap-9x" />
                <h3>
                  Materials & Finishes
                </h3>
                <div class="gap-3x" />
                <div class="line-break-gray" />
                <QuestionGroup
                  :key="material[0]"
                  v-for="material in materialfinish[0].materialfinish"
                  klass="product-detail-input"
                  :question="material[0]"
                  :ref="material[0]"
                  :noBorder="true"
                  :error="errors[material[0]]"
                >
                  <div @click="() => openSelectModal(material)">
                    <div>{{formattedDetails(material)}}</div>
                    <div class="gap-1x" />
                    <div class="link active medium-small-text bold">Edit</div>
                  </div>
                </QuestionGroup>
              </PreviewSection>

              <PreviewSection title="Financing">
                <StatusWithNote
                  v-bind:status.sync="financing.status"
                  v-bind:note.sync="financing.note"
                  :error="errors.financing"
                  placeholder="Financing Options — 500 characters maximum"
                />
              </PreviewSection>

              <PreviewSection title="Customization">
                <StatusWithNote
                  v-bind:status.sync="customization.status"
                  v-bind:note.sync="customization.note"
                  :error="errors.customization"
                  placeholder="Customization Options — 500 characters maximum"
                />
              </PreviewSection>

              <PreviewSection title="Contact Vendor">
                <div class="body-16">Reach out to {{organizationName}} with a quick email letting them know you’re interested in this design.</div>
                <div class="gap-3x" />
                <SymbiumButton
                  :text="contactButtonText"
                  :onClick="openContactModal"
                  kclass="contact-button"
                  :isSmall="true"
                />
              </PreviewSection>

              <PreviewSection title="ADU Report">
                <div class="body-16">Your free ADU Report includes information about your property, key regulations, and the steps to getting your ADU designed, approved, and built.</div>
                <div class="gap-3x" />
                <SymbiumButton
                  text="ADU Report"
                  :onClick="openReportModal"
                  kclass="contact-button"
                  :isSmall="true"
                />
              </PreviewSection>
              <div class="gap-3x" />
            </div>
            <div class="gap-3x" />
          </div>
        </div>

        <div class="section compact-product-listing black">
          <h2 class="section-field no-border-top">Compact Product Listing</h2>
          <div class="line-break-gray" />
          <div class="gap-3x" />
          <div v-if="loading">
            <div class="gap-3x" />
            <Spinner />
            <div class="gap-3x" />
          </div>
          <div v-if="!loading">
            <div class="section-field product-form gray-border">
              <div class="flex-box">
                <div class="info-left">
                  <ProfileImageContainer
                    :imageUrl="imageURLs[0]"
                    klass="compact"
                    @manage-image="openImageUploadModal"
                  />
                </div>
                <div class="info-right adu-foot-print" @click="openDrawingTool" >
                  <ProfileImageContainer
                    :imageUrl="aduFootPrint"
                    defaultText="SKETCH BUILDING FOOTPRINT"
                    hoverText="SKETCH BUILDING FOOTPRINT"
                    klass="adu-footprint"
                  />
                  <div class="error-message" v-if="errors.aduFootPrint">{{errors.aduFootPrint}}</div>
                </div>
              </div>
              <div class="gap-3x" />
              <div class="bold">{{name}} by {{organizationName}}</div>
              <div class="gap-1x" />
              <div class="">{{sellingpoint.join(' · ')}}</div>
              <div class="gap-1x" />
              <div class="small-text gray">
                <span class="margin-right-2x bold">{{materialfinish[0].producttype}}</span>
                <span>{{bedroom}} Bed · {{bathroom}} Bath</span>
              </div>
            </div>
          </div>
          <div class="gap-3x" />
        </div>

        <div class="section black">
          <h2 class="section-field no-border-top">Product Availability</h2>
          <div class="line-break-gray" />
          <div v-if="loading">
          <div class="gap-3x" />
            <Spinner />
            <div class="gap-3x" />
          </div>
          <div v-if="!loading">
            <div class="row no-gutters">
              <div class="col-lg-6 col-xl-6 col-md-6 col-sm-12">
                <div class="service-area">
                  <div class="bold med-sm-text">
                    Service Area
                  </div>
                  <div class="gap-3x" />
                  <div class="gray">
                    Type the name of the California counties or cities where your design or services are offered. If a user searches for an address outside your service area, a special tag will be shown indicating it is unavailable at that address. By not selecting any service area, the default selection will be all available counties in California.
                  </div>
                  <div class="gap-3x" />
                  <AutocompleteTagInput
                    :tagsList="serviceAreas"
                    @tags-changed="(newTags) => serviceAreas = newTags"
                  />
                </div>

              </div>
              <div class="col-lg-6 col-xl-6 col-sm-6 col-md-6 col-sm-12">
                <div class="pre-plans">
                  <div class="bold med-sm-text">
                    Pre-approved Plans
                  </div>
                  <div class="gap-3x" />
                  <div class="gray">
                    Type the name of the California counties where your design is pre-approved by a local government.  We will display a special tag indicating pre-approved status for addresses in those counties.
                  </div>
                  <div class="gap-3x" />
                  <AutocompleteTagInput
                    :tagsList="preapprovedRegions"
                    @tags-changed="(newTags) => preapprovedRegions = newTags"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
        <FooterHelper />
      </div>
    </div>

    <RoomSelectModal @save="saveRoomsData" />
    <SelectModal @save="saveSelectData" />
    <MessageModal />
    <DrawingModal @save="saveDrawing" />
    <ImageUploadModal @upload-finished="uploadFinished" @show-error="displayErrorModal" />
    <ConfirmModal />
  </div>
</template>

<script>
import _ from 'lodash'
import {Money} from 'v-money'

import EventBus from 'src/components/EventBus.js'
import Header from 'src/components/dashboard/shares/Header.vue'
import FooterHelper from 'src/components/dashboard/shares/FooterHelper.vue'
import Spinner from 'src/components/common/Spinner.vue'
import InputWithErrorV2 from "src/components/common/InputWithErrorV2.vue";
import AutoResizeInput from "src/components/common/AutoResizeInput.vue";
import QuestionGroup from "src/components/common/QuestionGroup.vue";
import RoomSelectModal from "src/components/common/modals/product/RoomSelectModal.vue";
import ImageUploadModal from "src/components/common/modals/product/ImageUploadModal.vue";
import SelectModal from "src/components/common/modals/product/SelectModal.vue";
import MessageModal from "src/components/common/modals/MessageModal.vue";
import ConfirmModal from "src/components/common/modals/ConfirmModal.vue";
import FullScreenSpinner from 'src/components/common/FullScreenSpinner.vue'
import StatusWithNote from "./components/StatusWithNote.vue";
import Navigation from "./components/Navigation.vue";
import DrawingModal from "./components/DrawingModal.vue";
import ProfileImageContainer from "./components/ProfileImageContainer.vue";
import ListingPreview from "./ListingPreview.vue";
import TimeStamp from "./components/TimeStamp.vue";
import PreviewSection from './components/PreviewSection.vue'
// import RegionConfig from './components/RegionConfig.vue'
import AutocompleteTagInput from 'src/components/common/form/AutocompleteTagInput.vue'
import SymbiumButton from 'src/components/common/SymbiumButton.vue'

import DataValidation from "src/mixins/DataValidation.js"
import DisplayFormError from 'src/mixins/DisplayFormError.js'
import HandleUnexpectedError from 'src/mixins/HandleUnexpectedError.js'
import ScreenSizeWatcher from 'src/mixins/ScreenSizeWatcher.js'

export default {
  components: {
    Header, FooterHelper, Spinner, InputWithErrorV2, DrawingModal, TimeStamp,
    AutoResizeInput, QuestionGroup, RoomSelectModal, Navigation,
    SelectModal, StatusWithNote, MessageModal, FullScreenSpinner, ImageUploadModal,
    ProfileImageContainer, ListingPreview, ConfirmModal, Money, PreviewSection,
    AutocompleteTagInput, SymbiumButton
  },
  mixins: [
    ScreenSizeWatcher, DisplayFormError, HandleUnexpectedError, DataValidation
  ],
  props: {
    preupload: {
      type: Boolean,
      default: false,
    }
  },
  methods: {
    addPaddingTop() {
      this.$refs.productDetailFormContainer.style["padding-top"] = '72px'
      this.$refs.productDetailFormContainer.style["margin-top"] = '0'
    },
    removePaddingTop() {
      this.$refs.productDetailFormContainer.style["padding-top"] = '0'
      this.$refs.productDetailFormContainer.style["margin-top"] = '0'
    },
    checkSuspendedVendor(data) {
      this.isSuspendedVendor = data.is_suspended_vendor
    },
    destroyProduct() {
      this.$modal.hide('confirm-modal')
      this.fullScreenLoading = true;
      this.$design.destroyDesign(this.designId, this.backToListing, this.displayErrorModal)
    },
    backToListing() {
      this.$router.push(`/dashboard/listings?vendorId=${this.vendorId}`)
    },
    openDestroyModal() {
      if(this.designId) {
        this.$modal.show('confirm-modal', {
          actionText: 'Delete Product',
          confirmMessage: 'Permanently delete this product?',
          confirmCallBack: this.destroyProduct
        })
      } else {
        this.backToListing()
      }
    },
    openUnpublishModal() {
      this.$modal.show('confirm-modal', {
        actionText: 'Unpublish This Product',
        confirmMessage: 'Remove this product from Symbium Build?',
        confirmCallBack: this.unpublishProduct
      })
    },
    validateBeforePublish() {
      this.validateNumber({ attribute: 'includes', value: _.size(this.materialfinish[0].includes), min: 1 })
      _.each(this.materialfinish[0].materialfinish, (material) => {
        // min value is 2 since first element is name of material
        this.validateNumber({ attribute: material[0], value: _.size(material), min: 2 })
      });
      this.validateStringLength({ attribute: 'name', max: 25, min: 1 })
      this.validateNumber({ attribute: 'images', value: _.size(this.images), min: 1 })
      this.validateNumber({ attribute: 'area', upperMessage: "5000sqft maximum", lowerMessage: "1sqft minimum", value: parseInt(this.area), min: 1, max: 5000 })
      this.validateNumber({ attribute: 'maxHeight', upperMessage: "40ft maximum", lowerMessage: "1ft minimum", value: parseInt(this.maxHeight), min: 1, max: 40 })
      this.validateNumber({
        attribute: 'cost', max: 999999, min: 1, lowerMessage: "$1 minimum",
        upperMessage: "$999,999 maximum", value: this.materialfinish[0].cost
      })
      this.validateStringLength({ attribute: 'sellingpoint', max: 50, min: 1, value: this.formattedSellingpoint })
      this.validateStringLength({
        attribute: 'description',
        max: 1500, min: 1,
      })
      if(this.financing.status == "Available") this.validateStringLength(
        {
          attribute: 'financing', max: 500, min: 1, value: this.financing.note
        }
      )
      if(this.customization.status == "Available") this.validateStringLength(
        {
          attribute: 'customization', max: 500, min: 1, value: this.customization.note
        }
      )
      this.validateRequireField({ attribute: 'producttype', value: this.materialfinish[0].producttype })
      this.validateRequireField({ attribute: 'aduFootPrint' })
      this.$nextTick(() => {
        this.$forceUpdate()
        this.showErrors()
      })
      return _.values(this.errors).every(_.isEmpty)
    },
    openPublishDraftModal() {
      if(!this.validateBeforePublish()) return;
      this.$modal.show('confirm-modal', {
        actionText: 'Publish',
        confirmMessage: 'Publish this product to Symbium Build?',
        confirmCallBack: this.publishDraft,
        alert: false,
      })
    },
    publishDraft() {
      this.$modal.hide('confirm-modal')
      if(!this.validate()) return;
      this.fullScreenLoading = true;
      this.$design.publishDraft(
        this.designId,
        { DATA: this.$data },
        this.assignDesignData,
        this.displayErrorModal
      )
    },
    unpublishProduct() {
      this.$modal.hide('confirm-modal')
      this.fullScreenLoading = true;
      this.$design.unpublishProduct(
        this.designId,
        this.assignDesignData,
        this.displayErrorModal
      )
    },
    assignVendorData(data) {
      this.organizationName = data.vendorName
      this.email = data.email
      this.loading = false

      this.addAsset()
    },
    addAsset() {
      window.detailedListingVueComponent = this
      // Add number field and dropdown
      this.$nextTick(() => {
        const assetScript = document.createElement('script')

        assetScript.text = `
          new InputField({
            id: "building-area-field",
            suffix: "sqft",
            onChange: (value) => window.detailedListingVueComponent.area = value,
            placeholder: "500",
            value: ${this.area},
            validationCallback: (value) => Number.isInteger(value)
          });
          new InputField({
            id: "building-height-field",
            suffix: "ft",
            onChange: (value) => window.detailedListingVueComponent.maxHeight = value,
            placeholder: "15",
            value: ${this.maxHeight},
            validationCallback: (value) => Number.isInteger(value)
          });
          new MultiDropdown({
            id: "product-type-dropdown",
            placeholder: "Product type",
            options: [
              { id: "Prefab Building", option: "Prefab Building" },
              { id: "Site-build Building", option: "Site-build Building" },
              { id: "Drawings", option: "Drawings" },
            ],
            selectOption: (value) => window.detailedListingVueComponent.materialfinish[0].producttype = value.id,
            isMulti: false,
          });`
        document.head.appendChild(assetScript)
      })
    },
    openRoomSelectModal() {
      this.$modal.show('room-select-modal', {
        numberOfBedrooms: this.bedroom,
        numberOfBathRooms: this.bathroom,
        rooms: this.rooms,
      })
    },
    saveRoomsData(data) {
      this.bedroom = data.numberOfBedrooms
      this.bathroom = data.numberOfBathRooms
      this.rooms = data.rooms
    },
    openSelectModal(options) {
      this.$modal.show('select-modal', {
        selectedOptions: options.slice(1, options.length),
        name: options[0],
      })
    },
    saveSelectData(data) {
      if(data.attribute == "Includes") {
        this.materialfinish[0].includes = data.selectedOptions
        this.validateNumber({ attribute: 'includes', value: _.size(this.materialfinish[0].includes), min: 1 })
      } else {
        var index = _.findIndex(this.materialfinish[0].materialfinish, (material) => {
          return material[0] == data.attribute
        })
        this.materialfinish[0].materialfinish[index] = [data.attribute].concat(data.selectedOptions)
      }
      _.each(this.materialfinish[0].materialfinish, (material) => {
        // min value is 2 since first element is name of material
        this.validateNumber({ attribute: material[0], value: _.size(material), min: 2 })
      });
      this.$forceUpdate()
    },
    formattedDetails(details) {
      if(!details || !Array.isArray(details)) return
      return details.slice(1, details.length).join(', ');
    },
    openReportModal() {
      this.$modal.show('message-modal', {
        headerMessage: 'ADU Report',
        bodyMessage: 'When the user clicks on this button, they will be directed to the ADU Report page.  This page has useful information about the development standards that will impact their future project.  It also contains some basic specification about your product, including size and height.'
      })
    },
    openContactModal() {
      this.$modal.show('message-modal', {
        headerMessage: this.contactButtonText,
        bodyMessage: "When the user clicks on this button, they will have the ability to send a message directly to your organization. The message will be delivered to your organization's point of contact email: " + `<strong>${this.email}</strong>`,
      })
    },
    openImageUploadModal() {
      this.$modal.show(
        'image-upload-modal',
        _.pick(this.$data, ['imageURLs', 'designId', 'images', 'vendorId'])
      )
    },
    saveDraft() {
      this.fullScreenLoading = true
      if(this.designId) {
        this.$design.updateDesign(this.designId, { DATA: this.$data }, this.displayErrorModal)
      } else {
        this.$design.createDesign({ DATA: this.$data, vendorId: this.vendorId }, this.assignDesignData, this.displayErrorModal)
      }
    },
    assignDesignData(design) {
      var data = design.data
      this.fullScreenLoading = false
      var attributes = [
        "designId", "name", "sellingpoint", "description", "area",
        "maxHeight", "bedroom", "bathroom", "rooms", "imageURLs",
        "financing", "customization", "images", "materialfinish", "dim",
        "bbox", "aduFootPrint", "footPrintSize", "updated_at", "published_at",
        'status', "aduDrawingPoints", "preapprovedRegions", "serviceAreas",
        "vendorId", "needReplace"
      ]
      Object.assign(this.$data, _.pick(data, attributes))
      this.need_image_process = false
      this.$router.replace({query: {id: this.designId, vendorId: this.vendorId }});
    },
    openDrawingTool() {
      if(this.windowWidth > 760) {
        this.$modal.show('drawing-modal', { aduDrawingPoints: this.aduDrawingPoints })
      } else {
        this.$modal.show('message-modal', {
          headerMessage: 'Building Footprint',
          bodyMessage: 'Drawing or editing a building footprint is only available on tablets or desktops.'
        })
      }

    },
    saveDrawing(data) {
      this.dim = data.dimension.dim
      this.bbox = data.dimension.bbox
      this.aduDrawingPoints = data.dimension.aduDrawingPoints
      this.aduFootPrint = data.aduFootPrint
      this.footPrintSize = data.footPrintSize
      this.need_adu_process = true
    },
    uploadFinished(data) {
      if(!data.designId) return

      this.designId = data.designId
      this.images = data.images
      this.fullScreenLoading = true
      this.need_image_process = !data.no_need_image_s3_processing
      this.$design.updateDesign(this.designId, { DATA: this.$data }, this.displayErrorModal);
    },
    validate() {
      this.showErrors()
      return _.values(this.errors).every(_.isEmpty)
    },
    displayErrorModal(e) {
      this.fullScreenLoading = false
      if(e.error) {
        this.$modal.show('message-modal', {
          headerMessage: 'Error',
          bodyMessage: e.error,
          clickToClose: false,
          beforeClose: () => this.$router.push('/dashboard/account_overview')
        })
      } else {
        EventBus.$emit('server-unexpected-error')
      }
    }
  },
  computed: {
    formattedRooms() {
      if(this.bedroom == 0 && this.bathroom == 0 && this.rooms.length == 0) {
        return false
      }
      var allRooms = [`Bedroom ${this.bedroom}`, `Bathroom ${this.bathroom}`].concat(this.rooms)
      return allRooms.join(', ')
    },
    contactButtonText() {
      return `Contact ${this.organizationName}`;
    },
    includesMaterialData() {
      return ["Includes"].concat(this.materialfinish[0].includes)
    },
    formattedSellingpoint() {
      return this.sellingpoint.join(' · ')
    },
    nameTextCount() {
      return _.size(this.name);
    }
  },
  watch: {
    sellingpoint() {
      this.validateStringLength({ attribute: 'sellingpoint', min: 1, max: 50, value: this.formattedSellingpoint })
    },
    description() {
      this.validateStringLength({ attribute: 'description', min: 1, max: 1500 })
    },
    area() {
      this.area = parseInt(this.area)
      this.validateNumber({ attribute: 'area', upperMessage: "5000sqft maximum", lowerMessage: "1sqft minimum", value: parseInt(this.area), min: 1, max: 5000 })
    },
    maxHeight() {
      this.maxHeight = parseInt(this.maxHeight)
      this.validateNumber({ attribute: 'maxHeight', upperMessage: "40ft maximum", lowerMessage: "1ft minimum", value: this.maxHeight, min: 1, max: 40 })
    },
    name() {
      this.validateStringLength({ attribute: 'name', min: 1, max: 25 })
    },
    rooms() {
      if(this.formattedRooms) this.errors.rooms = null
    },
    images() {
      if(_.size(this.images) > 0) this.errors.images = null
    },
    aduFootPrint() {
      if(_.size(this.aduFootPrint) > 0) this.errors.aduFootPrint = null
    },
    materialfinish: {
      handler: function(val) {
        if(val[0].producttype) this.errors.producttype = null
        this.validateNumber({
          attribute: 'cost', max: 999999, min: 1, lowerMessage: "$1 minimum",
          upperMessage: "$999,999 maximum", value: this.materialfinish[0].cost
        })
      },
      deep: true,
    },
    financing: {
      handler: function(val) {
        if(val.status == "Available") this.validateStringLength({ attribute: 'financing', max: 500, value: val.note })
      },
      deep: true,
    },
    customization: {
      handler: function(val) {
        if(val.status == "Available") this.validateStringLength({ attribute: 'customization', max: 500, value: val.note })
      },
      deep: true,
    }
  },
  data() {
    return {
      isSuspendedVendor: false,
      organizationName: null,
      name: "",
      sellingpoint: [],
      preapprovedRegions: [],
      serviceAreas: [],
      description: "",
      area: null,
      maxHeight: null,
      bedroom: 0,
      bathroom: 0,
      rooms: [],
      loading: false,
      materialfinish: [{
        materialfinish: [
          ["Exterior"], ["Window & Door Frames"], ["General Interior"],
          ["Kitchen"], ["Bathroom"], ["Included Appliances"]
        ],
        producttype: null,
        includes: [],
        cost: 0,
      }],
      financing: { status: 'Unavailable' },
      customization: { status: 'Unavailable' },
      imageURLs: [],
      images: [], // image names
      dim: [],
      bbox: [],
      aduDrawingPoints: [],
      aduFootPrint: null,
      fullScreenLoading: false,
      designId: null,
      footPrintSize: null,
      updated_at: null,
      published_at: null,
      status: 'Unsaved Draft',
      preview: false,
      email: null,
      need_image_process: false,
      need_adu_process: false,
      vendorId: null,
      needReplace: false,
      moneyOption: {
         thousands: ',',
         prefix: '$ ',
         precision: 0,
         masked: false
       }
    }
  },
  mounted() {
    this.loading = true,
    this.$user.fetchMe();
    this.designId = this.$route.query.id
    this.vendorId = this.$route.query.vendorId
    if(this.designId) {
      this.fullScreenLoading = true
      this.$design.fetchDesign(this.designId);
    }

    this.$design.getContactPoint(this.vendorId, this.assignVendorData)
  },
  created() {
    EventBus.$on('design-fetched', this.assignDesignData)
    EventBus.$on('user-fetched', this.checkSuspendedVendor)
  },
  beforeDestroy() {
    EventBus.$off('design-fetched', this.assignDesignData)
    EventBus.$off('user-fetched', this.checkSuspendedVendor)
  },
}
</script>

<style lang="scss">
.input-field {
  input {
    padding: 0 !important;
    border: none !important;
  }
}
.product-detail {
  .dashboard-content-container {
    padding: 0;
    margin-top: 0;
  }

  .dashboard-content-middle.single-section {
    width: unset;
    margin: 0px 24px;
  }

  .contact-button {
    float: none;
  }

  input {
    width: 100%;
  }

  .product-form {
    background: white;
    margin: auto;
    width: 552px;
    padding: 24px !important;
  }

  .service-area {
    padding: 32px;
    padding-left: 0;
    border-right: 1px solid #DDDDDD;
  }
  .pre-plans {
    padding: 32px;
    padding-right: 0;
  }

  .big-body {
    input {
      font-weight: 600;
      font-size: 36px !important;
      line-height: 48px !important;
      letter-spacing: -0.405px;
    }
  }

  .adu-text {
    width: 80px;
    margin: auto;
    text-align: center;
    margin-top: 48px;
  }

  .product-detail-input {
    .calc-left {
      width: 240px;
      flex: initial;
    }

    .calc-right {
      width: 216px;
      flex: initial;
      text-align: left;
    }

    .text-3x {
      line-height: 40px;
      margin-left: 8px;
    }

    .question-text {
      height: 100%;
      font-weight: 600;
    }

    .area-input input {
      width: 88px !important;
      font-size: 24px !important;
      line-height: 32px !important;
    }
    .height-input input {
      width: 72px !important;
      font-size: 24px !important;
      line-height: 32px !important;
    }
    .product-type.select {
      width: 216px;
    }


  }

  .do-more {
    .button-left {
      margin-right: 16px;
    }
  }

  .info-right {
    min-width: 144px;
  }

  .info-left {
    flex-grow: 1;
    max-width: 288px;
    margin-right: 24px;
  }

  @media screen and (max-width: 768px) {
    .dashboard-content-container {
      margin-top: 0px;
    }

    .dashboard-content-middle.single-section {
      margin-top: 32px;
    }

    .section-field.product-form {
      padding: 0 24px;
      width: 100%;
      max-width: 504px;
    }

    .service-area {
      border-right: none;
      padding-right: 0;
    }
    .pre-plans {
      padding-left: 0;
    }

    .big-body {
      font-size: 36px;
      line-height: 48px;
      letter-spacing: -0.405px;
    }

    .oval-button.contact-button {
      padding: 16px 32px;
    }

    .product-detail-input {
      flex-wrap: wrap;

      .calc-left {
        margin-top: 8px;
        max-width: 168px;
        margin-right: 24px;
      }
    }

    .do-more {
      display: block;

      .oval-button {
        float: none;
      }
    }

    .compact-product-listing {
      .info-left {
        max-width: initial;
      }

      .info-right {
        min-width: initial;
      }
    }

    .adu-foot-print.info-left {
      width: calc(100% - 100px);
      margin-right: 12px;
    }
  }
}
</style>
